if (!customElements.get('tabbed-info')) {
const FADE_DURATION_MS = 180;

const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches || false;

const clearFadeState = (element) => {
  if (!element) return;
  if (element.fadeTimeoutId) {
    window.clearTimeout(element.fadeTimeoutId);
    element.fadeTimeoutId = null;
  }
  element.classList.remove('is-fading-in', 'is-fading-out');
};

const showElementWithFade = (element, { skipAnimation = false } = {}) => {
  if (!element) return;
  clearFadeState(element);
  element.hidden = false;

  if (skipAnimation || prefersReducedMotion()) return;

  element.classList.add('is-fading-in');
  window.requestAnimationFrame(() => {
    element.classList.remove('is-fading-in');
  });
};

const hideElementWithFade = (element, { skipAnimation = false } = {}) => {
  if (!element) return;
  clearFadeState(element);

  if (element.hidden) {
    element.hidden = true;
    return;
  }

  if (skipAnimation || prefersReducedMotion()) {
    element.hidden = true;
    return;
  }

  element.classList.add('is-fading-out');
  element.fadeTimeoutId = window.setTimeout(() => {
    element.classList.remove('is-fading-out');
    element.hidden = true;
    element.fadeTimeoutId = null;
  }, FADE_DURATION_MS);
};

const swapElementsWithFade = (currentElement, nextElement, { skipAnimation = false } = {}) => {
  if (!nextElement) return;

  if (!currentElement || currentElement === nextElement || currentElement.hidden) {
    showElementWithFade(nextElement, { skipAnimation });
    return;
  }

  clearFadeState(currentElement);
  clearFadeState(nextElement);

  if (skipAnimation || prefersReducedMotion()) {
    currentElement.hidden = true;
    nextElement.hidden = false;
    return;
  }

  currentElement.classList.add('is-fading-out');
  currentElement.fadeTimeoutId = window.setTimeout(() => {
    currentElement.classList.remove('is-fading-out');
    currentElement.hidden = true;
    currentElement.fadeTimeoutId = null;
    showElementWithFade(nextElement);
  }, FADE_DURATION_MS);
};

const fadeUpdateElement = (element, updateCallback, { skipAnimation = false } = {}) => {
  if (!element) {
    if (typeof updateCallback === 'function') updateCallback();
    return;
  }

  clearFadeState(element);

  if (skipAnimation || prefersReducedMotion()) {
    if (typeof updateCallback === 'function') updateCallback();
    return;
  }

  element.classList.add('is-fading-out');
  element.fadeTimeoutId = window.setTimeout(() => {
    element.classList.remove('is-fading-out');
    if (typeof updateCallback === 'function') updateCallback();
    element.fadeTimeoutId = null;
    showElementWithFade(element);
  }, FADE_DURATION_MS);
};

const initTabbedInfo = (tabbedInfo) => {
  if (!tabbedInfo || tabbedInfo.dataset.tabbedInfoReady === 'true') return;

  const tabButtons = Array.from(tabbedInfo.querySelectorAll('.tabbed-info__tab-button'));
  const tabPanels = Array.from(tabbedInfo.querySelectorAll('.tabbed-info__tabpanel'));

  const setPanelUnit = (panel, unit = 'in', options = {}) => {
    const { skipAnimation = false } = options;
    const normalizedUnit = unit === 'cm' ? 'cm' : 'in';
    const unitButtons = panel.querySelectorAll('.tabbed-info__unit-button');
    const measurementsTable = panel.querySelector('.size-table--measurements');

    unitButtons.forEach((button) => {
      const isActive = button.dataset.unit === normalizedUnit;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', `${isActive}`);
    });

    const applyUnit = () => {
      if (measurementsTable?.setMeasurementsUnit) {
        measurementsTable.setMeasurementsUnit(normalizedUnit);
      } else if (measurementsTable) {
        measurementsTable.dataset.measurements = normalizedUnit === 'cm' ? 'cm' : 'inch';
      }
    };

    if (!measurementsTable || measurementsTable.hidden) {
      applyUnit();
      return;
    }

    const currentUnit = measurementsTable.dataset.measurements === 'cm' ? 'cm' : 'in';
    if (currentUnit === normalizedUnit) {
      applyUnit();
      return;
    }

    fadeUpdateElement(measurementsTable, applyUnit, { skipAnimation });
  };

  const setPanelView = (panel, view = 'conversions', options = {}) => {
    const { skipAnimation = false } = options;
    const normalizedView = view === 'conversions' ? 'conversions' : 'measurements';
    const conversionsTable = panel.querySelector('.size-table--conversions');
    const measurementsTable = panel.querySelector('.size-table--measurements');
    const conversionDescription = panel.querySelector('.conversion-description');
    const measurementsDescription = panel.querySelector('.measurements-description');
    const viewButtons = panel.querySelectorAll('.tabbed-info__view-button');
    const unitToggle = panel.querySelector('.tabbed-info__unit-toggle');

    viewButtons.forEach((button) => {
      const isActive = button.dataset.view === normalizedView;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', `${isActive}`);
    });

    const currentTable = [conversionsTable, measurementsTable].find((table) => table && !table.hidden);
    const targetTable = normalizedView === 'conversions' ? conversionsTable : measurementsTable;

    if (targetTable && currentTable !== targetTable) {
      swapElementsWithFade(currentTable, targetTable, { skipAnimation });
    } else if (targetTable) {
      showElementWithFade(targetTable, { skipAnimation });
    } else if (currentTable) {
      hideElementWithFade(currentTable, { skipAnimation });
    }

    if (conversionDescription) conversionDescription.hidden = normalizedView !== 'conversions';
    if (measurementsDescription) measurementsDescription.hidden = normalizedView !== 'measurements';

    if (unitToggle) {
      if (normalizedView === 'measurements' && measurementsTable) {
        showElementWithFade(unitToggle, { skipAnimation });
      } else {
        hideElementWithFade(unitToggle, { skipAnimation });
      }
    }
  };

  tabPanels.forEach((panel) => {
    const defaultUnit = panel.dataset.defaultUnit || 'in';
    const defaultView = panel.dataset.defaultView || 'conversions';

    panel.querySelectorAll('.tabbed-info__view-button').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        setPanelView(panel, button.dataset.view);
      });
    });

    panel.querySelectorAll('.tabbed-info__unit-button').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        setPanelUnit(panel, button.dataset.unit);
      });
    });

    setPanelUnit(panel, defaultUnit, { skipAnimation: true });
    setPanelView(panel, defaultView, { skipAnimation: true });
  });

  const selectTab = (selectedButton, options = {}) => {
    const { skipAnimation = false } = options;
    const selectedPanelId = selectedButton.getAttribute('aria-controls');
    const selectedPanel = tabPanels.find((tabPanel) => tabPanel.id === selectedPanelId);
    if (!selectedPanel) return;

    tabButtons.forEach((tabButton) => {
      const isActive = tabButton === selectedButton;
      tabButton.setAttribute('aria-selected', `${isActive}`);
      tabButton.classList.toggle('is-active', isActive);
    });

    const currentPanel = tabPanels.find((tabPanel) => !tabPanel.hidden);
    tabPanels.forEach((tabPanel) => {
      if (tabPanel !== selectedPanel && tabPanel !== currentPanel) {
        tabPanel.hidden = true;
      }
    });

    if (currentPanel && currentPanel !== selectedPanel) {
      swapElementsWithFade(currentPanel, selectedPanel, { skipAnimation });
      return;
    }

    showElementWithFade(selectedPanel, { skipAnimation });
  };

  tabButtons.forEach((tabButton) => {
    tabButton.addEventListener('click', (event) => {
      event.preventDefault();
      selectTab(tabButton);
    });
  });

  if (tabButtons.length > 0 && tabPanels.length > 0) {
    selectTab(tabButtons[0], { skipAnimation: true });
  }

  tabbedInfo.dataset.tabbedInfoReady = 'true';
};

class TabbedInfo extends HTMLElement {
  constructor() {
    super();
    initTabbedInfo(this);
  }

  connectedCallback() {
    initTabbedInfo(this);
  }
}

customElements.define('tabbed-info', TabbedInfo);
window.initTabbedInfo = initTabbedInfo;
}
